import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box, Paper, Typography, TextField, Button, Alert, Stack, MenuItem,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  IconButton, Divider
} from '@mui/material';
import { Save, Cancel, Add, Delete } from '@mui/icons-material';
import { salesService } from '../../services/sales.service';
import { customerService } from '../../services/customer.service';
import settingsService from '../../services/settings.service';
import { useLanguage } from '../../i18n/LanguageContext';
import { Customer, Invoice, InvoiceLine } from '../../types';

const emptyLine: InvoiceLine = { description: '', quantity: 1, unitPrice: 0, taxRate: 0, lineTotal: 0 };

const InvoiceForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [currencies, setCurrencies] = useState<any[]>([]);
  const [currencyCode, setCurrencyCode] = useState('USD');

  const [invoice, setInvoice] = useState<Partial<Invoice>>({
    customerId: 0,
    dueDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    invoiceLines: [{ ...emptyLine }],
  });

  useEffect(() => {
    loadData();
    if (id) loadInvoice();
  }, [id]);

  const loadData = async () => {
    try {
      const custData = await customerService.getAll();
      setCustomers(custData);
    } catch (err) { console.error('Error loading customers:', err); }

    try {
      const currRes = await settingsService.getCurrencies();
      setCurrencies(currRes.data);
      const def = currRes.data.find((c: any) => c.isDefault);
      if (def && !id) setCurrencyCode(def.code);
    } catch (err) { console.error('Currencies not available:', err); }
  };

  const loadInvoice = async () => {
    try {
      const data = await salesService.getInvoiceById(Number(id));
      setInvoice(data);
      if ((data as any).currencyCode) setCurrencyCode((data as any).currencyCode);
    } catch (err) { setError('Failed to load invoice'); }
  };

  const sym = currencies.find(c => c.code === currencyCode)?.symbol || '$';

  const calculateLineTotal = (line: InvoiceLine): number => {
    const subtotal = line.quantity * line.unitPrice;
    return subtotal + subtotal * (line.taxRate / 100);
  };

  const calculateTotals = () => {
    const lines = invoice.invoiceLines || [];
    const subTotal = lines.reduce((sum, l) => sum + l.quantity * l.unitPrice, 0);
    const taxAmount = lines.reduce((sum, l) => sum + l.quantity * l.unitPrice * (l.taxRate / 100), 0);
    return { subTotal, taxAmount, totalAmount: subTotal + taxAmount };
  };

  const handleLineChange = (index: number, field: keyof InvoiceLine, value: string | number) => {
    const lines = [...(invoice.invoiceLines || [])];
    lines[index] = { ...lines[index], [field]: typeof value === 'string' && field !== 'description' ? Number(value) : value };
    lines[index].lineTotal = calculateLineTotal(lines[index]);
    setInvoice({ ...invoice, invoiceLines: lines });
  };

  const addLine = () => setInvoice({ ...invoice, invoiceLines: [...(invoice.invoiceLines || []), { ...emptyLine }] });
  const removeLine = (i: number) => {
    const lines = [...(invoice.invoiceLines || [])];
    if (lines.length > 1) { lines.splice(i, 1); setInvoice({ ...invoice, invoiceLines: lines }); }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    if (!invoice.customerId) { setError('Please select a customer'); setLoading(false); return; }
    if (!invoice.invoiceLines?.length) { setError('Please add at least one line item'); setLoading(false); return; }

    const totals = calculateTotals();
    try {
      await salesService.createInvoice({
        ...invoice,
        currencyCode, exchangeRate: 1,
        subTotal: totals.subTotal, taxAmount: totals.taxAmount, totalAmount: totals.totalAmount,
        invoiceLines: invoice.invoiceLines?.map(l => ({ ...l, lineTotal: calculateLineTotal(l) })),
      } as any);
      navigate('/invoices');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save invoice');
    } finally { setLoading(false); }
  };

  const totals = calculateTotals();

  return (
    <Box>
      <Typography variant="h4" gutterBottom>{id ? t('invoices.edit') : t('invoices.new')}</Typography>
      <Paper sx={{ p: 3 }}>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Box component="form" onSubmit={handleSubmit}>
          <Stack spacing={3}>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField fullWidth select label={t('sales_orders.customer')} value={invoice.customerId || ''} required disabled={loading}
                onChange={e => setInvoice({ ...invoice, customerId: Number(e.target.value) })}>
                <MenuItem value={0} disabled>Select a customer</MenuItem>
                {customers.map(c => <MenuItem key={c.id} value={c.id}>{c.name} ({c.code})</MenuItem>)}
              </TextField>
              <TextField fullWidth type="date" label={t('invoices.due_date')} value={invoice.dueDate || ''} required disabled={loading}
                onChange={e => setInvoice({ ...invoice, dueDate: e.target.value })} InputLabelProps={{ shrink: true }} />
              <TextField select label={t('common.currency')} value={currencyCode} sx={{ minWidth: 150 }} disabled={loading}
                onChange={e => setCurrencyCode(e.target.value)}>
                {currencies.map((c: any) => <MenuItem key={c.id} value={c.code}>{c.code} ({c.symbol})</MenuItem>)}
                {currencies.length === 0 && <MenuItem value="USD">USD ($)</MenuItem>}
              </TextField>
            </Box>

            <Divider />
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">{t('invoices.line_items')}</Typography>
                <Button variant="outlined" startIcon={<Add />} onClick={addLine} size="small">{t('common.add_line')}</Button>
              </Box>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold', width: '35%' }}>{t('common.description')}</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', width: '12%' }} align="right">Quantity</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', width: '15%' }} align="right">{t('common.unit_price')}</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', width: '12%' }} align="right">{t('sales_orders.tax_pct')}</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', width: '18%' }} align="right">Line Total</TableCell>
                      <TableCell sx={{ width: '8%' }} />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(invoice.invoiceLines || []).map((line, index) => (
                      <TableRow key={index}>
                        <TableCell><TextField fullWidth size="small" placeholder="Description" value={line.description}
                          onChange={e => handleLineChange(index, 'description', e.target.value)} disabled={loading} /></TableCell>
                        <TableCell><TextField fullWidth size="small" type="number" value={line.quantity}
                          onChange={e => handleLineChange(index, 'quantity', e.target.value)} inputProps={{ min: 1 }} disabled={loading} /></TableCell>
                        <TableCell><TextField fullWidth size="small" type="number" value={line.unitPrice}
                          onChange={e => handleLineChange(index, 'unitPrice', e.target.value)} inputProps={{ min: 0, step: 0.01 }} disabled={loading} /></TableCell>
                        <TableCell><TextField fullWidth size="small" type="number" value={line.taxRate}
                          onChange={e => handleLineChange(index, 'taxRate', e.target.value)} inputProps={{ min: 0, max: 100 }} disabled={loading} /></TableCell>
                        <TableCell align="right"><Typography variant="body2" sx={{ fontWeight: 'bold' }}>{sym}{calculateLineTotal(line).toFixed(2)}</Typography></TableCell>
                        <TableCell align="center">
                          <IconButton size="small" color="error" onClick={() => removeLine(index)} disabled={(invoice.invoiceLines || []).length <= 1}>
                            <Delete fontSize="small" /></IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>

            <Divider />
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Box sx={{ width: 300 }}>
                <Stack spacing={1}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography>Subtotal:</Typography><Typography>{sym}{totals.subTotal.toFixed(2)}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography>Tax:</Typography><Typography>{sym}{totals.taxAmount.toFixed(2)}</Typography>
                  </Box>
                  <Divider />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="h6">Total:</Typography><Typography variant="h6">{sym}{totals.totalAmount.toFixed(2)}</Typography>
                  </Box>
                </Stack>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button type="submit" variant="contained" startIcon={<Save />} disabled={loading}>
                {loading ? 'Saving...' : 'Save Invoice'}</Button>
              <Button variant="outlined" startIcon={<Cancel />} onClick={() => navigate('/invoices')} disabled={loading}>{t('common.cancel')}</Button>
            </Box>
          </Stack>
        </Box>
      </Paper>
    </Box>
  );
};

export default InvoiceForm;
